import { Pipe, PipeTransform, Injectable } from '@angular/core'
import { CenterMeasurementUnitsService } from '../centers/shared/center.measurement-units.service'
import { MeasurementUnits } from './measurement-units.model'

interface Convertor {
  short: string
  convertedUnitName: string
  ratio: number
  shortConvertedUnitName: string
  offset?: number
}

@Injectable()
abstract class ClassWithUnitType {
  constructor(private centerMeasurementUnitsService: CenterMeasurementUnitsService) {
    this.initMeasurementUnit(this.centerMeasurementUnitsService)
  }

  unitType: MeasurementUnits = MeasurementUnits.metric

  setUnitType = (unitType: MeasurementUnits) => {
    this.unitType = unitType
  }

  get isMetric(): boolean {
    return this.unitType === MeasurementUnits.metric
  }

  handleFetchMeasurementUnitError(): void {
    console.warn('Error getting measurement units for center. Conversion pipe defaulting to metric system.', this)
  }

  private initMeasurementUnit(centerMeasurementUnitsService: CenterMeasurementUnitsService): void {
    centerMeasurementUnitsService.centerMeasurementUnits$.subscribe(
      this.setUnitType,
      this.handleFetchMeasurementUnitError.bind(this))
  }
}

const UNITS_CONVERSION: Record<string, Convertor> = {
  meter: { short: 'm', convertedUnitName: 'foot', ratio: 3.2808399, shortConvertedUnitName: 'ft' },
  kilogram: { short: 'kg', convertedUnitName: 'pound', ratio: 2.20462262, shortConvertedUnitName: 'lb' },
  kilometer: { short: 'km', convertedUnitName: 'mile', ratio: 0.621371192, shortConvertedUnitName: 'mi' },
  celsius: { short: '°C', convertedUnitName: 'fahrenheit', ratio: 1.8, offset: 32, shortConvertedUnitName: '°F' },
  'meter-per-second': {
    short: 'm/s',
    convertedUnitName: 'mile-per-hour',
    ratio: 2.23693629,
    shortConvertedUnitName: 'mph',
  },
}

function convertUnits(value: number, convertor: Convertor): number {
  value *= convertor?.ratio ?? 1
  value += convertor?.offset ?? 0
  return value
}

/*
 * Formats given number value by rounding and adding measurement units.
 * null or undefined will be converted to an optional empty value.
 *
 * Examples:
 *    {{ null| units:'celsius'           :0: 'No data' }} : -> 'No data'
 *    {{ 14.8| units:'celsius'           :0: 'No data' }} : -> '15°C'
 *    {{    1| units:'kilometer-per-hour':2: 'No data' }} : -> '1.00 km/h'
 */
@Pipe({ name: 'measurementUnit' })
export class MeasurementUnitsPipe extends ClassWithUnitType implements PipeTransform {
  transform = (
    value: number | undefined | null,
    measurementUnit: string,
    decimalPlaces: number | null,
    emptyValue = '',
  ): string => {
    if ([null, undefined, NaN].includes(value)) {
      return emptyValue
    }

    if (!this.isMetric) {
      const convertor = UNITS_CONVERSION[measurementUnit]
      measurementUnit = convertor?.convertedUnitName ?? measurementUnit
      value = convertUnits(value, convertor)
    }

    return new Intl.NumberFormat('en-US', {
      style: 'unit',
      // @ts-ignore
      unit: measurementUnit,
      ...(decimalPlaces === null
        ? null
        : { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces }),
    }).format(value)
  }
}

@Pipe({ name: 'measurementUnitConvert' })
export class MeasurementUnitConvertPipe extends ClassWithUnitType implements PipeTransform {
  transform = (value: number, measurementUnit: string): number =>
    this.isMetric ? value : convertUnits(value, UNITS_CONVERSION[measurementUnit])
}

@Pipe({ name: 'unitName' })
export class UnitNamePipe extends ClassWithUnitType implements PipeTransform {

  transform = (measurementUnit: string): string => {
    const convertor = UNITS_CONVERSION[measurementUnit]
    return this.isMetric ? convertor.short : convertor.shortConvertedUnitName
  }
}

@Pipe({ name: 'reverseUnitConversion' })
export class MaybeReverseConvertPipe extends ClassWithUnitType implements PipeTransform {

  transform = (value: number | null, measurementUnit: string): number | null => {
    if (value === null || this.isMetric) {
      return value
    }

    const convertor = UNITS_CONVERSION[measurementUnit]
    value -= convertor?.offset ?? 0
    value /= convertor?.ratio ?? 1
    return value
  }
}
